import { useMemo, useState } from 'react';

import { useDismissableOverlay } from '@/hooks/components/useDismissibleOverlay';
import { useColumnVisibility } from '@/hooks/tables/useTableColumnsVisibility';
import { useTableSortAndPage } from '@/hooks/tables/useTableSortAndPage';

type Props<K extends string> = {
  allColumns: readonly ColumnDef<K>[];
  initialSort: { key: K; dir: SortDir };
  resetDeps?: unknown[];
};

export function useTableUiControls<K extends string>({ allColumns, initialSort, resetDeps = [] }: Props<K>) {
  const { filteredColumns, visibleColumns, toggleColumn } = useColumnVisibility(allColumns);
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const { ref: columnsOverlayRef } = useDismissableOverlay(columnSelectorOpen, () => setColumnSelectorOpen(false));

  const { page, setPage, sort, toggleSort } = useTableSortAndPage<K>({
    initialSort,
    resetDeps,
  });

  const columns = useMemo(
    () =>
      allColumns.map((column) => ({
        ...column,
        visible: visibleColumns.has(column.key),
      })),
    [allColumns, visibleColumns],
  );

  const visibleHeadColumns = useMemo(() => columns.filter((column) => column.visible), [columns]);

  return {
    columnSelectorOpen,
    columns,
    columnsOverlayRef,
    filteredColumns,
    page,
    setPage,
    sort,
    toggleColumn,
    toggleSort,
    visibleHeadColumns,
    openColumnSelector: () => setColumnSelectorOpen(true),
  };
}
